import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';

const ThreadSelectionPanel = ({ 
  isOpen,
  onClose,
  onSuccess,
  onError,
  onProcessingStart,
  onProcessingEnd,
  selectedReports,
  templateId
}) => {
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState(0);

    const resetPanelState = () => {
    setSelectedThreadId(null);
    setThreads([]);
    setSearchInput('');
    setSearch('');
    setPage(1);
    setTotal(0);
  };


  // Fetch threads when panel opens or search/pagination changes
  useEffect(() => {
    if (isOpen) {
      fetchThreads();
    }
  }, [isOpen, page, size, search]);

  const fetchThreads = async () => {
  setLoading(true);
  try {
    const response = await axios.post(
      '/pceapi/genbi/list-threads-for-append',
      {
        template_id: templateId,
        page: page,
        size: size,
        search: search || null,
        filter_type: null
      },
      {
        headers: {
          Authorization: GetCookie('access_token')
        }
      }
    );

    if (response.data && response.data.status === 'success') {
      const responseData = response.data.data;
      setThreads(responseData.threads || []);
      setTotal(responseData.total_threads || 0);
    } else {
      setThreads([]);
      setTotal(0);
      if (onError) {
        onError(response.data?.message || 'Failed to fetch threads');
      }
    }
  } catch (error) {
    console.error('Error fetching threads:', error);
    setThreads([]);
    setTotal(0);
    if (onError) {
      onError(error.response?.data?.message || 'Failed to fetch threads');
    }
  } finally {
    setLoading(false);
  }
};

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleThreadSelection = (threadId) => {
    setSelectedThreadId(threadId);
  };

  const handleMove = async () => {
    if (!selectedThreadId) {
      if (onError) {
        onError('Please select a thread');
      }
      return;
    }

    if (onProcessingStart) {
      onProcessingStart(`Migrating ${selectedReports.length} report(s) to thread...`);
    }

    try {
      const formData = new FormData();
      
      const threadData = threads.find(thread => thread.id === selectedThreadId);
      
      if (!threadData) {
        throw new Error('Selected thread not found');
      }
      
      formData.append('thread_id', selectedThreadId);
      formData.append('name', threadData.name || 'Untitled Thread');
      formData.append('thread_access_level', threadData.thread_access_level || 'PRIVATE');
      formData.append('tags', JSON.stringify(threadData.tags || []));
      formData.append('user_access', JSON.stringify(threadData.user_access || []));

      let successCount = 0;
      
      for (const report of selectedReports) {
        try {
          const downloadResponse = await axios.post(
            '/pceapi/genbi/download-report-for-append',
            {
              template_id: templateId,
              ver: report.ver
            },
            {
              headers: {
                Authorization: GetCookie('access_token')
              },
              responseType: 'blob'
            }
          );

          const filename = report.s3_path 
            ? report.s3_path.split('/').pop() 
            : `report_v${report.ver}.${report.file_type || 'docx'}`;
          
          const file = new File([downloadResponse.data], filename, {
            type: downloadResponse.data.type || 'application/octet-stream'
          });

          const fileType = report.file_type?.toLowerCase() || 'docx';
          
          if (fileType === 'pdf') {
            formData.append('pdfFiles', file);
          } else if (fileType === 'docx' || fileType === 'doc') {
            formData.append('wordFiles', file);
          } else if (fileType === 'pptx' || fileType === 'ppt') {
            formData.append('pptFiles', file);
          } else if (fileType === 'xlsx' || fileType === 'xls') {
            formData.append('excelFiles', file);
          } else if (fileType === 'txt') {
            formData.append('textFiles', file);
          } else if (fileType === 'csv') {
            formData.append('csvFiles', file);
          } else {
            formData.append('wordFiles', file);
          }

          successCount++;

        } catch (downloadError) {
          console.error(`Error downloading report V${report.ver}:`, downloadError);
        }
      }

      if (successCount === 0) {
        throw new Error('Failed to download any reports');
      }

      const response = await axios.post(
        '/pceapi/threads/unstructured/update',
        formData,
        {
          headers: {
            Authorization: GetCookie('access_token'),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.status === 'success') {
        onClose();
        if (onSuccess) {
          onSuccess(`${successCount} of ${selectedReports.length} report(s) Migrated to Data Collection successfully`);
        }
      } else {
        if (onError) {
          onError(response.data.message || 'Failed to append reports to Data Collection');
        }
      }
    } catch (error) {
      console.error('Error Migrating reports:', error);
      if (onError) {
        onError(error.response?.data?.message || error.message || 'Failed to append reports to Data Collection');
      }
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="position-absolute ev-message-card2 ev-card-3 ev-padding-small top-100 z-1000 px-3 pt-3" 
      style={{ marginTop: 'var(--px-12)',  width: 'var(--px-600)' }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="ev-bold-med ev-reg-p-blue ev-fs-14">
          Select Data Collection to Migrate {selectedReports.length} Report(s)
        </span>
        <button 
          type="button"
          className="btn-close" 
          onClick={() => {
          resetPanelState();
          onClose();
        }}
          aria-label="Close"
        ></button>
      </div>

      {/* Search and Pagination Row */}
      <div className="d-flex justify-content-between align-items-center margin-bottom-2">
        {/* Left side - Search */}
        <div className="col-5">
          <div className="position-relative">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search Data Collections"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
            <button 
              type="button"
              className="ev-btn-lightblue-fill-round search-bar-button" 
              onClick={handleSearch}
              disabled={loading}
            >
              <i className="bi bi-search"></i>
            </button>        
          </div>
        </div>

        {/* Right side - Pagination */}
        <div className="col-7 d-flex justify-content-end align-items-center">
          <div className="d-flex align-items-center">
            <button 
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page === 1 || loading}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            <span className="ev-xsmall-p-dark mx-2">
              Page {page}/{Math.ceil(total / size) || 1}
            </span>

            <button 
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page >= Math.ceil(total / size) || loading}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Thread List */}
      <div className="scrollable-element vh-25 margin-bottom">
        {loading ? (
          <div className="text-center py-2">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : threads.length === 0 ? (
          <p className="ev-small-p-blue text-center mt-2">No Data Collections found</p>
        ) : (
          <div className="thread-tree ms-1">
            {threads.map((thread) => (
              <div key={thread.id} className="margin-bottom-1">
                <div className="d-flex align-items-center">
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input "
                      type="radio"
                      id={`thread-${thread.id}`}
                      name="selectedThread"
                      checked={selectedThreadId === thread.id}
                      onChange={() => handleThreadSelection(thread.id)}
                    />
                    <label className="form-check-label" htmlFor={`thread-${thread.id}`}>
                      <span className="ev-small-p-blue">{thread.name}</span> &nbsp;
                      <span className={`badge ev-btn-small ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                        {thread.thread_access_level || 'PRIVATE'}
                      </span>
                      <span className="badge bg-secondary ms-1 ev-btn-xsmall">
                        {thread.document_count || 0} docs
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2">
        <button 
          type="button"
          className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
          onClick={handleMove}
          disabled={!selectedThreadId || loading}
        >
          <i className="bi bi-arrow-right-circle"></i> Migrate
        </button>

        <button 
          type="button"
          className="ev-btn-blue-outline-round ev-btn-small"
          onClick={() => {
          resetPanelState();
          onClose();
        }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ThreadSelectionPanel;